import { z } from 'zod';
import { LanguageSchema } from '../common/language.js';
import { CursorPaginationSchema } from '../common/pagination.js';
import { PeriodParamSchema, PeriodZoneSchema, zoneRequiredWith } from '../common/period.js';
import { csv } from '../common/query.js';
import { ImportFormatSchema } from '../import-export/envelopes.js';
import { AuditActionTypeSchema } from './actions.js';
import { AuditObjectParamSchema } from './entities.js';
// The channel a line came through — the stored `audit_logs.source`, written by
// the request that produced the row (CONVENTIONS §Audit): `ui` a person's web
// session · `pat` a person's own token · `app` a connected application (alone,
// or on behalf of a person) · `import` an import run's rows · `system` the
// platform with no actor (a job, a sweep, the bootstrap). The picker's order.
export const AUDIT_CHANNELS = ['ui', 'pat', 'app', 'import', 'system'];
export const AuditChannelSchema = z.enum(AUDIT_CHANNELS);
// The filters the trail and its export share — the page's URL keys, one for one
// (ADR-037 §2): `period` + `tz` (ADR-082 §5 · §6 — the one time axis, a word or
// a day pair cut in the PERSON's zone, which must come with it) · `action` (the
// six words) · `object` (the tree's tokens) · `person` (user ids, or `me` for
// the viewer — ADR-080 §6) · `channel` · `q` (the object's NAME — the stored
// label, by the shared fold) · `correlation` (the lines of one action — the
// peek's door). `entity_type` + `entity_id` together select the per-entity
// History subset (an entity's own trail).
const auditFilterFields = {
    period: PeriodParamSchema.optional(),
    tz: PeriodZoneSchema,
    action: csv(AuditActionTypeSchema),
    object: AuditObjectParamSchema,
    person: csv(z.string().min(1).max(16)).pipe(z.array(z.string()).max(50, 'at most 50 people').optional()),
    channel: csv(AuditChannelSchema),
    /** The subject's name: its words in any order, each at least three characters (the trigram floor — a shorter word
     *  is not a search), at most eight, each once; the server folds both sides. */
    q: z.string().trim().max(64).optional(),
    correlation: z.string().uuid().optional(),
    entity_type: z.string().min(1).max(64).optional(),
    entity_id: z.string().min(1).max(64).optional(),
};
/** GET /api/projects/:projectId/audit — the trail, keyset-paged (cursor + limit from the shared envelope). */
export const AuditQuerySchema = CursorPaginationSchema.extend(auditFilterFields)
    .strict()
    .superRefine(zoneRequiredWith(['period']));
/** GET /api/projects/:projectId/audit/export — the current view as one file (ADR-045: the file IS the screen), newest
 *  first, in the person's zone (ADR-082 §8); `format` + `lang` as every export; no cursor — the whole view, capped. */
export const AuditExportQuerySchema = z
    .object({
    format: ImportFormatSchema.default('xlsx'),
    lang: LanguageSchema.default('en'),
    ...auditFilterFields,
})
    .strict()
    .superRefine(zoneRequiredWith(['period']));
